import {
  Briefcase,
  Building2,
  Calendar,
  CheckSquare,
  Edit3,
  Hash,
  Mail,
  PenLine,
  Type,
  User,
} from 'lucide-react';
import type { DocumentSignFieldInput } from '@/lib/api';

type FieldType = DocumentSignFieldInput['type'];

/**
 * dataTransfer key carrying the field type while a palette item is dragged
 * onto a document page. Read by the editor's drop handler.
 */
export const FIELD_DRAG_MIME = 'application/x-document-sign-field';

interface FieldPaletteProps {
  /** Currently-armed type, or `null` when nothing is picked up. */
  selectedType: FieldType | null;
  onSelect: (type: FieldType) => void;
}

const TYPES: { type: FieldType; label: string; icon: typeof Edit3 }[] = [
  { type: 'signature', label: 'Signature', icon: PenLine },
  { type: 'initial', label: 'Initial', icon: Edit3 },
  { type: 'date_signed', label: 'Date Signed', icon: Calendar },
  { type: 'signer_name', label: 'Name', icon: User },
  { type: 'email', label: 'Email', icon: Mail },
  { type: 'company', label: 'Company', icon: Building2 },
  { type: 'title', label: 'Title', icon: Briefcase },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

/**
 * Field-type picker — a single-column rail beside the document, switching to
 * two columns when the layout narrows and the document stacks below it.
 *
 * Two ways to place a field: click a type to arm it (a preview then follows
 * the cursor; the next click on a page drops it), or drag a type straight
 * onto a page. The armed type is highlighted and announced via `aria-pressed`.
 */
export function FieldPalette({ selectedType, onSelect }: FieldPaletteProps) {
  return (
    <ul role="toolbar" aria-label="Field types" className="grid grid-cols-2 gap-1 md:grid-cols-1">
      {TYPES.map(({ type, label, icon: Icon }) => {
        const active = selectedType === type;
        return (
          <li key={type}>
            <button
              type="button"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(FIELD_DRAG_MIME, type);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => onSelect(type)}
              aria-pressed={active}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card hover:bg-accent border-transparent'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span>{label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export type { FieldType };
